import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Logout } from "./logout";
import { ShopLayout } from "@/components/layouts/ShopLayout";
import { ShopProductsLayout } from "@/components/layouts/ShopProductsLayout";
import { ShopGiftCards } from "./shop/giftCards";
import { ShopProducts } from "./shop/products";
import { ShopTravel } from "./shop/travel";
import { ShopCheckout } from "./shop/checkout";
import { ShopHistory } from "./shop/history";
import { ShopHistoryDetail } from "./shop/history/historyDetail";
import {
  TaskerMessagesReviewPage,
  MyTaskerMessagesPage,
  TaskerLayout,
  RegisterPhone,
} from "./tasker";
import { AdminLayout } from "@/components/layouts/AdminLayout";
import { CardInfo } from "./shop/giftCards/cardInfo";
import { NotFound } from "./notFound";
import { ProductInfo } from "./shop/products/productInfo";
import { TravelCardInfo } from "./shop/travel/cardInfo";
import { useAuthContext } from "@/context/AuthContext";
import { Shop } from "./shop";
import { Terms } from "./Terms";
import { TaskerProgramsPage } from "./tasker/programs";
import { CYCLayout } from "@/components/layouts/CYCLayout";
import { CYCProfile } from "./cyc/profile";
import { CYCLeaderboard } from "./cyc/leaderboard";
import { LiteRaveLayout } from "@/components/layouts/LiteRaveLayout";
import { LiteRaveClaim } from "./liteRaves/claim";
import { LiteRaveHome } from "./liteRaves/home";
import { LiteRaveCreate } from "./liteRaves/create";
import { LiteRavePresent } from "./liteRaves/present";
import { LiteRaveMyRaves } from "./liteRaves/my-raves";

// Lazy load super admin routes
const SuperAdminRoutes = lazy(() => import("./superAdmin/SuperAdminRoutes"));

// Lazy load raves routes
const RavesRoutes = lazy(() => import("./raves/RavesRoutes"));

export const AppRoutes = () => {
  const { user } = useAuthContext();

  return (
    <Routes>
      <Route index element={<Navigate to="/shop" replace />} />
      <Route path="/logout" element={<Logout />} />

      <Route element={<ShopLayout />}>
        <Route path="shop">
          <Route index element={<Shop />} />
          <Route path="giftCards" element={<ShopGiftCards />} />
          <Route path="travel" element={<ShopTravel />} />
          <Route path="giftCards/:brandKey" element={<CardInfo />} />
          <Route path="travel/:brandKey" element={<TravelCardInfo />} />
          <Route path="checkout" element={<ShopCheckout />} />
          <Route path="history" element={<ShopHistory />} />
          <Route path="history/:historyId" element={<ShopHistoryDetail />} />
          <Route path="terms" element={<Terms />} />
          <Route path="products" element={<ShopProductsLayout />}>
            <Route index element={<ShopProducts />} />
            <Route path=":productId" element={<ProductInfo />} />
          </Route>
        </Route>
        <Route path="/p" element={<RegisterPhone />} />
      </Route>

      <Route path="sendit" element={<TaskerLayout />}>
        <Route index element={<MyTaskerMessagesPage />} />
        <Route path="programs" element={<TaskerProgramsPage />} />
        {["manager", "admin", "super-admin"].includes(user.role) && (
          <Route
            path="approve-messages"
            element={<TaskerMessagesReviewPage />}
          />
        )}
      </Route>

      {user.role === "admin" ||
        (user.role === "super-admin" && (
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Admin />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="companies" element={<AdminCompanies />} />
            <Route path="regions" element={<AdminRegions />} />
          </Route>
        ))}

      {user.role === "super-admin" && (
        <Route
          path="/super-admin/*"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <SuperAdminRoutes />
            </Suspense>
          }
        />
      )}

      <Route
        path="/raves/*"
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <RavesRoutes />
          </Suspense>
        }
      />

      <Route path="/cyc" element={<CYCLayout />}>
        <Route index element={<Navigate to="/cyc/progress" replace />} />
        <Route path="progress" element={<CYCProfile />} />
        <Route path="leaderboard" element={<CYCLeaderboard />} />
      </Route>

      <Route path="/lite-raves" element={<LiteRaveLayout />}>
        <Route index element={<Navigate to="/lite-raves/home" replace />} />
        <Route path="claim" element={<LiteRaveClaim />} />
        <Route path="home" element={<LiteRaveHome />} />
        <Route path="create" element={<LiteRaveCreate />} />
        <Route path="my-raves" element={<LiteRaveMyRaves />} />
        <Route path=":raveId" element={<LiteRavePresent />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const Placeholder = () => {
  return <div>Placeholder</div>;
};

const Admin = Placeholder;

const AdminUsers = Placeholder;

const AdminCompanies = Placeholder;

const AdminRegions = Placeholder;
